import type { TableColumns } from '@ohpen/enso-ui';
import { AssistantPermissionType, FieldType, IntermediaryDraftDataRow, KeyValue } from '@pmp/api';

export type AssistantRow = {
  rowNumber: number;
  brokerRowNumber: number;
  assistantId: string;
  displayName: string;
  permissionType: AssistantPermissionType | KeyValue | null;
};

export type AdviserRow = {
  rowNumber: number;
  brokerRowNumber: number;
  adviserId: string;
  displayName: string;
  permissionType: AssistantPermissionType | KeyValue | null;
};

export const buildAssistantRows = (brokers: IntermediaryDraftDataRow[]): AssistantRow[] => {
  let rowNum = 0;
  return brokers.flatMap(broker =>
    (broker.assistants ?? []).map(({ assistantId, displayName, permissionType }) => ({
      rowNumber: rowNum++,
      brokerRowNumber: broker.rowNumber ?? 0,
      assistantId: assistantId ?? '',
      displayName: displayName ?? '',
      permissionType: permissionType ?? null,
    })),
  );
};

export const buildAdviserRows = (brokers: IntermediaryDraftDataRow[]): AdviserRow[] => {
  let rowNum = 0;
  return brokers.flatMap(broker =>
    (broker.advisers ?? []).map(({ adviserId, displayName, permissionType }) => ({
      rowNumber: rowNum++,
      brokerRowNumber: broker.rowNumber ?? 0,
      adviserId: adviserId ?? '',
      displayName: displayName ?? '',
      permissionType: permissionType ?? null,
    })),
  );
};

const toPermissionTypeKeyValue = (
  permissionType: AssistantPermissionType | null | undefined,
  permissionTypeLabels: Record<string, string>,
): KeyValue | null =>
  permissionType ? { key: permissionType, value: permissionTypeLabels[permissionType] ?? permissionType } : null;

export const buildAssistantsOriginalDataObject = (
  assistantRows: AssistantRow[],
  originalBrokers: IntermediaryDraftDataRow[],
  permissionTypeLabels: Record<string, string>,
): Record<string, AssistantRow> => {
  const acc: Record<string, AssistantRow> = {};
  for (const row of assistantRows) {
    const originalBroker = originalBrokers.find(b => (b.rowNumber ?? 0) === row.brokerRowNumber);
    const original = (originalBroker?.assistants ?? []).find(a => a.assistantId === row.assistantId);
    acc[row.rowNumber] = {
      ...row,
      displayName: original?.displayName ?? '',
      permissionType: toPermissionTypeKeyValue(original?.permissionType, permissionTypeLabels),
    };
  }
  return acc;
};

export const buildAdvisersOriginalDataObject = (
  adviserRows: AdviserRow[],
  originalBrokers: IntermediaryDraftDataRow[],
  permissionTypeLabels: Record<string, string>,
): Record<string, AdviserRow> => {
  const acc: Record<string, AdviserRow> = {};
  for (const row of adviserRows) {
    const originalBroker = originalBrokers.find(b => (b.rowNumber ?? 0) === row.brokerRowNumber);
    const original = (originalBroker?.advisers ?? []).find(a => a.adviserId === row.adviserId);
    acc[row.rowNumber] = {
      ...row,
      displayName: original?.displayName ?? '',
      permissionType: toPermissionTypeKeyValue(original?.permissionType, permissionTypeLabels),
    };
  }
  return acc;
};

export const assistantsCRColumns: TableColumns<AssistantRow> = [
  {
    title: 'lender.brokers.labels.assistantId',
    dataIndex: 'assistantId',
    key: 'assistantId',
    width: 320,
    options: { readOnly: true },
  },
  {
    title: 'lender.brokers.labels.name',
    dataIndex: 'displayName',
    key: 'displayName',
    width: 250,
    options: { readOnly: true },
  },
  {
    title: 'lender.brokers.labels.collaboration',
    dataIndex: 'permissionType',
    key: 'permissionType',
    width: 240,
    options: {
      format: { type: 'valueList' },
      listName: FieldType.ASSISTANT_PERMISSION_TYPE,
      required: true,
    },
  },
];

export const advisersCRColumns: TableColumns<AdviserRow> = [
  {
    title: 'lender.brokers.labels.adviserId',
    dataIndex: 'adviserId',
    key: 'adviserId',
    width: 320,
    options: { sorter: true, readOnly: true },
  },
  {
    title: 'lender.brokers.labels.name',
    dataIndex: 'displayName',
    key: 'displayName',
    width: 250,
    options: { readOnly: true },
  },
  {
    title: 'lender.brokers.labels.collaboration',
    dataIndex: 'permissionType',
    key: 'permissionType',
    width: 240,
    options: {
      format: { type: 'valueList' },
      listName: FieldType.ASSISTANT_PERMISSION_TYPE,
      required: true,
    },
  },
];
